/** @typedef {'starter'|'pro'|'gold'} SubscriptionPlanId */

/**
 * @typedef {{
 *   id: SubscriptionPlanId,
 *   priceEurMonthly: number,
 *   features: {
 *     billing: boolean,
 *     analytics: boolean,
 *     clubApps: boolean,
 *     clubInsights: boolean,
 *   },
 * }} SubscriptionPlan
 */

/** @type {readonly SubscriptionPlanId[]} */
export const SUBSCRIPTION_PLAN_IDS = Object.freeze(["starter", "pro", "gold"]);

/** @type {Readonly<Record<SubscriptionPlanId, SubscriptionPlan>>} */
export const SUBSCRIPTION_PLANS = Object.freeze({
  starter: Object.freeze({
    id: "starter",
    priceEurMonthly: 4.99,
    features: Object.freeze({
      billing: false,
      analytics: false,
      clubApps: false,
      clubInsights: false,
    }),
  }),
  pro: Object.freeze({
    id: "pro",
    priceEurMonthly: 9.99,
    features: Object.freeze({
      billing: true,
      analytics: true,
      clubApps: false,
      clubInsights: true,
    }),
  }),
  gold: Object.freeze({
    id: "gold",
    priceEurMonthly: 17.99,
    features: Object.freeze({
      billing: true,
      analytics: true,
      clubApps: true,
      clubInsights: true,
    }),
  }),
});

export const SUBSCRIPTION_TRIAL_DAYS = 30;
export const SUBSCRIPTION_TRIAL_BANNER_DAYS = 7;
export const SUBSCRIPTION_MAX_BONUS_TRAINERS = 5;
export const SUBSCRIPTION_MAX_FREE_DAYS = 180;

/**
 * Marketing-/Vergleichszeilen für Vereinsabo — abgeleitet aus SUBSCRIPTION_PLANS.
 * `planning` immer true; `finance` ≡ `billing`.
 * @typedef {{
 *   id: string,
 *   labelKey: string,
 *   included: Readonly<Record<SubscriptionPlanId, boolean>>,
 * }} SubscriptionFeatureRow
 */

/** @type {readonly SubscriptionFeatureRow[]} */
export const SUBSCRIPTION_FEATURE_ROWS = Object.freeze([
  Object.freeze({
    id: "planning",
    labelKey: "trainerstunden.subscription.feature.planning",
    included: Object.freeze({ starter: true, pro: true, gold: true }),
  }),
  Object.freeze({
    id: "billing",
    labelKey: "trainerstunden.subscription.feature.billing",
    included: Object.freeze({
      starter: SUBSCRIPTION_PLANS.starter.features.billing,
      pro: SUBSCRIPTION_PLANS.pro.features.billing,
      gold: SUBSCRIPTION_PLANS.gold.features.billing,
    }),
  }),
  Object.freeze({
    id: "finance",
    labelKey: "trainerstunden.subscription.feature.finance",
    included: Object.freeze({
      starter: SUBSCRIPTION_PLANS.starter.features.billing,
      pro: SUBSCRIPTION_PLANS.pro.features.billing,
      gold: SUBSCRIPTION_PLANS.gold.features.billing,
    }),
  }),
  Object.freeze({
    id: "analytics",
    labelKey: "trainerstunden.subscription.feature.analytics",
    included: Object.freeze({
      starter: SUBSCRIPTION_PLANS.starter.features.analytics,
      pro: SUBSCRIPTION_PLANS.pro.features.analytics,
      gold: SUBSCRIPTION_PLANS.gold.features.analytics,
    }),
  }),
  Object.freeze({
    id: "clubApps",
    labelKey: "trainerstunden.subscription.feature.clubApps",
    included: Object.freeze({
      starter: SUBSCRIPTION_PLANS.starter.features.clubApps,
      pro: SUBSCRIPTION_PLANS.pro.features.clubApps,
      gold: SUBSCRIPTION_PLANS.gold.features.clubApps,
    }),
  }),
]);

/**
 * @returns {readonly SubscriptionFeatureRow[]}
 */
export function getSubscriptionFeatureRows() {
  return SUBSCRIPTION_FEATURE_ROWS;
}

/**
 * Einmalige Bonus-Tage beim Erreichen der Trainer-Anzahl (kumulativ bis Cap).
 * 2 → +30 +60 on top; 3–5 → je +20 → Basis 30 + 150 = 180.
 * @type {Readonly<Record<number, number>>}
 */
export const SUBSCRIPTION_TRAINER_BONUS_DAYS_BY_COUNT = Object.freeze({
  2: 90,
  3: 20,
  4: 20,
  5: 20,
});

/**
 * @param {number} trainerCount
 * @returns {number} Summe aller Milestone-Boni bis zu diesem Count (cap 5)
 */
export function computeCumulativeTrainerBonusDays(trainerCount) {
  const n = Math.min(
    SUBSCRIPTION_MAX_BONUS_TRAINERS,
    Math.max(0, Math.floor(Number(trainerCount) || 0)),
  );
  let sum = 0;
  for (let c = 2; c <= n; c += 1) {
    sum += Number(SUBSCRIPTION_TRAINER_BONUS_DAYS_BY_COUNT[c]) || 0;
  }
  return Math.min(sum, SUBSCRIPTION_MAX_FREE_DAYS - SUBSCRIPTION_TRIAL_DAYS);
}

/**
 * Noch nicht angewendete Milestones und deren Bonus-Tage.
 * @param {number} trainerCount
 * @param {number[]} appliedMilestones
 * @returns {{ milestones: number[], bonusDays: number }}
 */
export function computePendingTrainerBonus(
  trainerCount,
  appliedMilestones = [],
) {
  const applied = new Set(
    (Array.isArray(appliedMilestones) ? appliedMilestones : [])
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x)),
  );
  const n = Math.min(
    SUBSCRIPTION_MAX_BONUS_TRAINERS,
    Math.max(0, Math.floor(Number(trainerCount) || 0)),
  );
  const milestones = [];
  let bonusDays = 0;
  for (let c = 2; c <= n; c += 1) {
    if (applied.has(c)) continue;
    const days = Number(SUBSCRIPTION_TRAINER_BONUS_DAYS_BY_COUNT[c]) || 0;
    if (days <= 0) continue;
    milestones.push(c);
    bonusDays += days;
  }
  return { milestones, bonusDays };
}

/**
 * Nächster Hinweis: wie viele Trainer bis zum nächsten Bonus.
 * @param {number} trainerCount
 * @param {number[]} appliedMilestones
 */
export function resolveNextTrainerBonusHint(
  trainerCount,
  appliedMilestones = [],
) {
  const count = Math.max(0, Math.floor(Number(trainerCount) || 0));
  const applied = new Set(
    (Array.isArray(appliedMilestones) ? appliedMilestones : []).map(Number),
  );
  for (let c = 2; c <= SUBSCRIPTION_MAX_BONUS_TRAINERS; c += 1) {
    if (applied.has(c) || count >= c) continue;
    return {
      nextTrainerCount: c,
      trainersNeeded: c - count,
      bonusDays: Number(SUBSCRIPTION_TRAINER_BONUS_DAYS_BY_COUNT[c]) || 0,
    };
  }
  return null;
}

/**
 * Kündigung wirksam: Ende der laufenden Kalenderwoche (So 23:59:59 Europe/Berlin).
 * @param {Date} [now]
 * @returns {string} ISO
 */
export function resolveCancelEffectiveAt(now = new Date()) {
  const berlinWall = new Date(
    now.toLocaleString("en-US", { timeZone: "Europe/Berlin" }),
  );
  const day = berlinWall.getDay();
  const add = day === 0 ? 0 : 7 - day;
  const end = new Date(berlinWall.getTime());
  end.setDate(end.getDate() + add);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}

/**
 * Anteilige Rückerstattung (Pilot-Schätzung): Tage nach Wirksamkeitsdatum × Tagespreis.
 * @param {{
 *   planId?: string,
 *   currentPeriodEnd?: string|null,
 *   cancelEffectiveAt?: string|null,
 * }} input
 */
export function computeRefundForCancel(input = {}) {
  const plan = getSubscriptionPlan(input.planId);
  const periodEndMs = input.currentPeriodEnd
    ? Date.parse(input.currentPeriodEnd)
    : Number.NaN;
  const effectiveMs = input.cancelEffectiveAt
    ? Date.parse(input.cancelEffectiveAt)
    : Number.NaN;
  if (!Number.isFinite(periodEndMs) || !Number.isFinite(effectiveMs)) {
    return { refundDays: 0, refundEurEstimate: 0 };
  }
  if (periodEndMs <= effectiveMs) {
    return { refundDays: 0, refundEurEstimate: 0 };
  }
  const refundDays = Math.ceil((periodEndMs - effectiveMs) / 86400000);
  const perDay = Number(plan.priceEurMonthly) / 30;
  const refundEurEstimate = Math.round(refundDays * perDay * 100) / 100;
  return { refundDays, refundEurEstimate };
}

/** @param {unknown} raw */
export function normalizeSubscriptionPlanId(raw) {
  const id = String(raw || "")
    .trim()
    .toLowerCase();
  // Legacy-Alias: früherer Plan-Name „club“ → „gold“
  if (id === "club") return "gold";
  return SUBSCRIPTION_PLAN_IDS.includes(/** @type {SubscriptionPlanId} */ (id))
    ? /** @type {SubscriptionPlanId} */ (id)
    : "starter";
}

/** @param {unknown} planId */
export function getSubscriptionPlan(planId) {
  const id = normalizeSubscriptionPlanId(planId);
  return SUBSCRIPTION_PLANS[id];
}

/**
 * Tabs, die Billing/Finanzen brauchen (Soft-Gate).
 * @param {string} tab
 */
export function isSubscriptionBillingTab(tab) {
  const key = String(tab || "").trim();
  return (
    key === "abrechnung" ||
    key === "abrechnen" ||
    key === "offenePosten" ||
    key === "rechnungen" ||
    key === "rechnungswesen" ||
    key === "finanzen" ||
    key === "zahlungen" ||
    key === "ausgabenerfassung" ||
    key === "bilanzierung"
  );
}

/** @param {string} tab */
export function isSubscriptionAnalyticsTab(tab) {
  return String(tab || "").trim() === "analytics";
}

/**
 * App-Keys, die ohne Club-Plan sichtbar bleiben (TrainingsDesk-Kern).
 * @type {readonly string[]}
 */
export const SUBSCRIPTION_CORE_APP_KEYS = Object.freeze(["trainerstunden"]);

/** @param {unknown} appKey */
export function isSubscriptionCoreAppKey(appKey) {
  const k = String(appKey || "")
    .trim()
    .toLowerCase();
  return (
    k === "trainerstunden" ||
    k === "coachdesk" ||
    k === "trainingsdesk" ||
    k === "hub-trainingsdesk"
  );
}

/**
 * „Weitere Club-Apps“ aus Launcher/Hub entfernen, wenn Plan kein clubApps hat.
 * Ausnahme: Club Insights bleibt mit features.clubInsights (Pro+) sichtbar.
 * @param {Iterable<string>|Set<string>|string[]|null|undefined} keys
 * @param {{ features?: { clubApps?: boolean, clubInsights?: boolean } }|null|undefined} access
 * @param {{ isSuperAdmin?: boolean }} [opts]
 * @returns {Set<string>}
 */
export function applySubscriptionToLauncherAppKeys(keys, access, opts = {}) {
  const src =
    keys instanceof Set
      ? keys
      : new Set(Array.isArray(keys) ? keys : [...(keys || [])]);
  // Kein Superadmin-Bypass: Nav/Launcher folgt dem Vereinsplan (Starter ≠ Gold).
  // Support-Zugriff bleibt über direkte URLs / Superadmin-Bereich möglich.
  void opts;
  if (access?.features?.clubApps) {
    return new Set(src);
  }
  const out = new Set();
  const allowInsights = Boolean(access?.features?.clubInsights);
  for (const k of src) {
    if (isSubscriptionCoreAppKey(k)) out.add("trainerstunden");
    const key = String(k || "")
      .trim()
      .toLowerCase();
    if (allowInsights && (key === "insights" || key === "statistik")) {
      out.add("insights");
    }
  }
  return out;
}

/**
 * Entfernt Nav-Tabs, die der aktuelle Plan nicht freischaltet.
 * @param {Iterable<string>|Set<string>|string[]} tabs
 * @param {{ features?: { billing?: boolean, analytics?: boolean }, showExpiredGate?: boolean, canWrite?: boolean }|null|undefined} access
 * @param {{ isSuperAdmin?: boolean }} [opts]
 * @returns {Set<string>}
 */
export function applySubscriptionToAllowedTabs(tabs, access, opts = {}) {
  const set = new Set(
    tabs instanceof Set
      ? [...tabs]
      : Array.isArray(tabs)
        ? tabs
        : [...(tabs || [])],
  );
  if (opts.isSuperAdmin) return set;
  const features = access?.features || {};
  if (!features.billing) {
    for (const tab of [...set]) {
      if (isSubscriptionBillingTab(tab)) set.delete(tab);
    }
  }
  if (!features.analytics) {
    set.delete("analytics");
  }
  return set;
}

/**
 * Hub-Finanzflächen an Plan koppeln.
 * @param {Record<string, boolean>|null|undefined} surface
 * @param {{ features?: { billing?: boolean } }|null|undefined} access
 * @param {{ isSuperAdmin?: boolean }} [opts]
 */
export function applySubscriptionToHubSurface(surface, access, opts = {}) {
  const base =
    surface && typeof surface === "object"
      ? { ...surface }
      : {
          showFinancePanel: false,
          showBillingContent: false,
          showPaymentsContent: false,
          showBillingAdminTasks: false,
          showMasterdataContent: true,
          showTrainerStatsPanel: false,
        };
  if (
    opts.isSuperAdmin ||
    access?.features?.billing ||
    opts.trainerHasExplicitBillingUiPermissions
  ) {
    return base;
  }
  return {
    ...base,
    showFinancePanel: false,
    showBillingContent: false,
    showPaymentsContent: false,
    showBillingAdminTasks: false,
    showTrainerStatsPanel: true,
  };
}

/**
 * Verwaltungs-Unterreiter, die Billing voraussetzen.
 * @param {string[]} subTabs
 * @param {{ features?: { billing?: boolean } }|null|undefined} access
 * @param {{ isSuperAdmin?: boolean }} [opts]
 * @returns {string[]}
 */
export function applySubscriptionToMasterdataSubTabs(
  subTabs,
  access,
  opts = {},
) {
  const list = Array.isArray(subTabs) ? [...subTabs] : [];
  if (opts.isSuperAdmin || access?.features?.billing) return list;
  return list.filter(
    (id) => id !== "invoiceLayout" && id !== "dataExport" && id !== "billing",
  );
}
